import { Component, inject, signal, computed, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '../../../../shared/material/material.module';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AccessPlan, Payment } from '../../../../core/generated';
import { ExternalPayerService, PaymentMethodType } from '../services/external-payer.service';
import { modalFadeIn } from '../../shared/animations';

export interface ConfirmPaymentModalData {
  plan: AccessPlan;
}

@Component({
  selector: 'app-confirm-payment-modal',
  standalone: true,
  imports: [CommonModule, MaterialModule, FormsModule],
  templateUrl: './confirm-payment-modal.component.html',
  styleUrl: './confirm-payment-modal.component.scss',
  animations: [modalFadeIn]
})
export class ConfirmPaymentModalComponent implements OnInit {
  dialogRef = inject(MatDialogRef<ConfirmPaymentModalComponent>);
  data = inject<ConfirmPaymentModalData>(MAT_DIALOG_DATA);

  private externalPayerService = inject(ExternalPayerService);
  private snackBar = inject(MatSnackBar);

  isLoading = signal(false);
  createdPayment = signal<Payment | null>(null);

  selectedMethod = signal<PaymentMethodType>('PIX');
  selectedInstallments = signal<number>(1);

  readonly installmentOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  readonly paymentMethods: { value: PaymentMethodType; label: string; icon: string }[] = [
    { value: 'PIX', label: 'PIX', icon: 'qr_code_2' },
    { value: 'BOLETO', label: 'Boleto', icon: 'receipt' },
    { value: 'CREDIT_CARD', label: 'Cartão de Crédito', icon: 'credit_card' },
  ];

  readonly installmentValue = computed(() => {
    const total = this.data.plan.finalTotal ?? 0;
    const n = this.selectedInstallments();
    return n > 0 ? total / n : total;
  });

  ngOnInit(): void {}

  onConfirm(): void {
    const planId = this.data.plan.id;
    if (!planId) return;

    this.isLoading.set(true);
    this.externalPayerService
      .confirmPayment(planId, this.selectedMethod(), this.selectedInstallments())
      .subscribe({
        next: (payment) => {
          this.createdPayment.set(payment);
          this.isLoading.set(false);
          this.snackBar.open('Pagamento confirmado! Parcelas geradas com sucesso.', 'Fechar', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
        },
        error: () => {
          this.snackBar.open('Erro ao confirmar o pagamento. Tente novamente.', 'Fechar', { duration: 5000 });
          this.isLoading.set(false);
        }
      });
  }

  close(): void {
    this.dialogRef.close(this.createdPayment());
  }

  formatCurrency(value?: number): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);
  }

  formatDate(dateString?: string): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR');
  }

  getInstallmentStatusClass(status?: string): string {
    const map: Record<string, string> = {
      'PENDING_PAYMENT': 'badge-warning',
      'PAID': 'badge-success',
      'LATE': 'badge-error',
      'CANCELLED_PAYMENT': 'badge-cancelled',
    };
    return map[status ?? ''] ?? 'badge-primary';
  }

  getInstallmentStatusLabel(status?: string): string {
    const map: Record<string, string> = {
      'PENDING_PAYMENT': 'Pendente',
      'PAID': 'Pago',
      'LATE': 'Atrasado',
      'CANCELLED_PAYMENT': 'Cancelado',
    };
    return map[status ?? ''] ?? status ?? '';
  }
}
